import { Box, BoxProps, Divider, Stack, Typography } from '@mui/material'
import Currency from '@/core/components/presentationStandards/Currency.tsx'
import Tasa from '@/core/components/presentationStandards/Tasa.tsx'
import SummaryBlock from '@/core/components/FinancialSummary/SummaryBlock.tsx'
import Percentage from '@/core/components/presentationStandards/Percentage.tsx'
import { useMemo } from 'react'
import DateTime from '@/core/components/presentationStandards/DateTime.tsx'
import RenderWithPermissions from '@/core/components/renderWithPermissions/RenderWithPermissions.tsx'

interface Props {
  additional_cost: string | number
  initial_cash?: string | number | null
  initial_wallet?: string | number | null
  initial: string | number
  payment_extra: string | number
  payment_extra_cash?: string | number | null
  payment_extra_wallet?: string | number | null
  initial_total: string | number
  payment_cuotes: string | number
  total_payment: string | number
  tasa: string | number
  rentability: string | number
  list_cuotes: { date: string; quote: string | number }[]
  descountPercent?: string | number
  discountedCredit?: string | number | null
  listCuotesMaxHeigh?: string
  policyValue?: string | number | null
  layout?: 'column' | 'row'
}

const FinancialSummary = ({
  additional_cost,
  initial_cash,
  initial_wallet,
  initial,
  payment_extra,
  payment_extra_cash,
  payment_extra_wallet,
  initial_total,
  payment_cuotes,
  total_payment,
  tasa,
  rentability,
  list_cuotes,
  descountPercent,
  discountedCredit,
  listCuotesMaxHeigh,
  policyValue,
  layout = 'column',
  ...props
}: Props & BoxProps) => {
  const quotesList = useMemo(
    () => (
      <>
        {list_cuotes?.map((item) => (
          <Box
            component="div"
            key={item.date}
            display="flex"
            justifyContent="space-between"
            flexDirection="row"
            gap={2}
          >
            <span>
              <strong>Fecha: </strong>
              <DateTime field={item.date} />
            </span>
            <Stack direction="row" spacing={1}>
              <strong>Pago: </strong>
              <Currency field={item.quote} />
            </Stack>
          </Box>
        ))}
      </>
    ),
    [list_cuotes]
  )
  const infoSection = (
    <Box>
      <Typography variant="subtitle1" gutterBottom>
        <strong>Información Financiera</strong>
      </Typography>
      <SummaryBlock
        label="Adicional"
        value={<Currency field={additional_cost} />}
      />
      {initial_cash !== null && initial_cash !== undefined && (
        <SummaryBlock
          label="Inicial en efectivo"
          value={<Currency field={initial_cash} />}
        />
      )}
      {initial_wallet !== null && initial_wallet !== undefined && (
        <SummaryBlock
          label="Inicial en mi bolsillo"
          value={<Currency field={initial_wallet} />}
        />
      )}
      <SummaryBlock label="Inicial" value={<Currency field={initial} />} />
      {descountPercent && (
        <SummaryBlock
          label="Inicial de oferta"
          value={<Percentage field={descountPercent} />}
        />
      )}
      {discountedCredit !== null && discountedCredit !== undefined && (
        <SummaryBlock
          label="Linea de crédito"
          value={<Currency field={discountedCredit} />}
        />
      )}
      {payment_extra_cash !== null && payment_extra_cash !== undefined && (
        <SummaryBlock
          label="Efectivo"
          value={<Currency field={payment_extra_cash} />}
        />
      )}
      {payment_extra_wallet !== null && payment_extra_wallet !== undefined && (
        <SummaryBlock
          label="Mi bolsillo"
          value={<Currency field={payment_extra_wallet} />}
        />
      )}
      <SummaryBlock
        label="Abono Extra"
        value={<Currency field={payment_extra} />}
      />
      <SummaryBlock
        label="Total Inicial"
        value={<Currency field={initial_total} />}
      />
      <SummaryBlock
        label="Pago por Cuota"
        value={<Currency field={payment_cuotes} />}
      />
      <SummaryBlock
        label="Total a Pagar"
        value={<Currency field={total_payment} />}
      />
      <RenderWithPermissions requiredPermissions={['SALES_PRODUCTS:SENSIBLE']}>
        <SummaryBlock label="Tasa" value={<Tasa field={tasa} />} />
        <SummaryBlock
          label="Rentabilidad"
          value={<Currency field={rentability} />}
        />
      </RenderWithPermissions>
      {policyValue !== null && policyValue !== undefined ? (
        <SummaryBlock
          label="Weppa Plus"
          value={
            policyValue === 0 ? 'Gratis' : <Currency field={policyValue} />
          }
        />
      ) : (
        ''
      )}
    </Box>
  )

  const quotesSection = (
    <Box>
      <Typography variant="subtitle1" sx={{ mt: layout === 'row' ? 0 : 2 }}>
        <strong>Cuotas</strong>
      </Typography>
      {list_cuotes.length === 0 && (
        <Typography variant="subtitle2">No hay cuotas calculadas</Typography>
      )}
      <Stack
        gap={0.5}
        sx={{ overflowY: 'auto', maxHeight: listCuotesMaxHeigh }}
      >
        {quotesList}
      </Stack>
    </Box>
  )

  if (layout === 'row') {
    return (
      <Box {...props}>
        <Stack
          direction="row"
          spacing={4}
          divider={<Divider orientation="vertical" flexItem />}
        >
          {infoSection}
          {quotesSection}
        </Stack>
      </Box>
    )
  }

  return (
    <Box {...props}>
      {infoSection}
      <Divider />
      {quotesSection}
    </Box>
  )
}

export default FinancialSummary
